import React from 'react';
import { Film, GripVertical, Image as ImageIcon, Repeat, Trash2 } from 'lucide-react';
import { formatPrescription } from '../../services/planDiff';

// Uma linha do plano em CARTÃO: miniatura automática (nunca um URL escrito à mão), prescrição editável no próprio
// cartão (sem formulário denso) e as ações que se usam (trocar, remover). O que o cartão mostra é o que o atleta vê.

const num = (v, fallback = 0) => (v === '' || v === null || v === undefined ? fallback : Number(v));

/** Um valor com − / + : escreve-se ou toca-se, sem abrir nada. */
function Stepper({ label, value, onChange, min = 0, step = 1, max = 999, suffix, testId }) {
  const current = num(value);
  const set = (next) => onChange(String(Math.min(max, Math.max(min, next))));
  return (
    <div className="ps-step">
      <span className="ps-step-label">{label}</span>
      <div className="ps-step-box">
        <button type="button" onClick={() => set(current - step)} aria-label={`Menos ${label}`} disabled={current - step < min}>−</button>
        <input
          type="text" inputMode="numeric" value={value === '' || value === null || value === undefined ? '' : value}
          onChange={(e) => onChange(e.target.value.replace(suffix === 'kg' ? /[^0-9.,]/g : /[^0-9]/g, ''))}
          aria-label={label} data-testid={testId}
        />
        <button type="button" onClick={() => set(current + step)} aria-label={`Mais ${label}`} disabled={current + step > max}>+</button>
      </div>
    </div>
  );
}

export default function ExerciseCard({
  exercise, index, selected, editing = true,
  onSelect, onFieldChange, onReplace, onRemove, onMove, onPreview, draggable = true,
}) {
  const media = exercise.gifUrl || exercise.imageUrl;
  const isTime = exercise.trackingType === 'time';
  const flags = [];

  if (exercise.exerciseLibraryId) flags.push({ key: 'lib', label: 'Da biblioteca' });
  else flags.push({ key: 'free', label: 'Escrito à mão' });
  if (!exercise.exerciseLibraryId && (exercise.setsTarget === '' || exercise.setsTarget === null) && !exercise.prescriptionSummary) flags.push({ key: 'rx', label: 'Sem prescrição' });
  if (exercise.isLegacy) flags.push({ key: 'legacy', label: 'Ainda sem ligação à biblioteca' });
  if (exercise.replacedAt) flags.push({ key: 'replaced', label: exercise.originalName ? `Substituído (era ${exercise.originalName})` : 'Substituído' });

  return (
    <div
      className={`ps-ex ${selected ? 'is-on' : ''}`}
      data-testid={`plan-ex-${index}`}
      draggable={draggable && editing}
      onDragStart={(e) => onMove?.(e)}
      onClick={() => onSelect?.()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.(); } }}
    >
      <span className="ps-ex-thumb" aria-hidden="true">
        {media ? <img alt="" src={media} loading="lazy" /> : (isTime ? '⏱️' : '🏋️')}
        {exercise.videoUrl || exercise.gifUrl ? <span className="ps-ex-media" title="Tem demonstração"><Film size={11} /></span> : null}
      </span>

      <span className="ps-ex-main">
        <span className="ps-ex-name">{exercise.name || 'Novo exercício'}</span>
        <span className="ps-ex-rx">{exercise.prescriptionSummary || formatPrescription(exercise) || 'Sem prescrição'}</span>

        {editing ? (
          <span className="ps-steps" onClick={(e) => e.stopPropagation()}>
            <Stepper label="Séries" value={exercise.setsTarget} min={1} onChange={(v) => onFieldChange('setsTarget', v)} testId={`rx-sets-${index}`} />
            <Stepper label={isTime ? 'Seg. mín.' : 'Reps mín.'} value={exercise.repsMin} min={1} onChange={(v) => onFieldChange('repsMin', v)} testId={`rx-repsmin-${index}`} />
            <Stepper label={isTime ? 'Seg. máx.' : 'Reps máx.'} value={exercise.repsMax} min={1} onChange={(v) => onFieldChange('repsMax', v)} testId={`rx-repsmax-${index}`} />
            <Stepper label="Descanso (s)" value={exercise.restSeconds} min={0} step={15} max={900} onChange={(v) => onFieldChange('restSeconds', v)} testId={`rx-rest-${index}`} />
            {!isTime ? (
              <Stepper label="Carga (kg)" value={exercise.targetWeightKg} min={0} step={2.5} max={600} suffix="kg" onChange={(v) => onFieldChange('targetWeightKg', v)} testId={`rx-kg-${index}`} />
            ) : null}
          </span>
        ) : null}

        <span className="ps-ex-flags">
          {flags.map((f) => <span key={f.key} className="ps-chip" style={{ cursor: 'default', padding: '2px 8px', fontSize: 11 }}>{f.label}</span>)}
        </span>
      </span>

      <span className="ps-day-actions" onClick={(e) => e.stopPropagation()}>
        {onPreview && exercise.exerciseLibraryId ? (
          <button type="button" className="ps-chip" onClick={() => onPreview(exercise)} title="Ver como se faz" aria-label={`Ver como se faz: ${exercise.name}`} data-testid={`plan-demo-${index}`}>
            <ImageIcon size={13} aria-hidden="true" />
          </button>
        ) : null}
        {onReplace && exercise.exerciseLibraryId ? (
          <button type="button" className="ps-chip" onClick={() => onReplace(exercise)} title="Trocar exercício" aria-label={`Trocar ${exercise.name}`} data-testid={`plan-swap-${index}`}>
            <Repeat size={13} aria-hidden="true" />
          </button>
        ) : null}
        {editing && onRemove ? (
          <button type="button" className="ps-chip" onClick={() => onRemove()} title="Remover" aria-label={`Remover ${exercise.name}`} data-testid={`plan-remove-${index}`}>
            <Trash2 size={13} aria-hidden="true" />
          </button>
        ) : null}
        {draggable && editing ? <GripVertical size={15} aria-hidden="true" style={{ color: 'var(--text-secondary)' }} /> : null}
      </span>
    </div>
  );
}
